import { getUpdatedAxisRangeBasedOnTicks } from '@thoughtspot/formatter-service';
import _ from 'lodash';
import { getAxisProperties, getChartProperties, getChartType, getColorBySerieName, getSeriesColorsMap, getUpdatedSeriesColor, getXAxes, getYAxes, isZoomedIn, } from '../core/chart-model-utils';
import * as chartTypeSpecificationService from '../core/chart-type-specification-service';
import { configOptions, hasNativeLegend, hasPersistentLegend, } from '../core/chart-type-specification-service';
import { getDefaultLabelEnablementValues, } from '../core/chart-ui-utils';
import { getContextMenuInputPoint } from '../core/context-menu-point-utils';
import { getSerie } from '../data/data-util';
import { getEnvFlags } from '../env';
import { BlinkHighchart } from '../highcharts/blink-highchart';
import { getHighchartScreenConfig } from '../highcharts/chart-theme-screen-service';
import { bindAxisEvents } from '../highcharts/highchart-axis-renderer';
import { getHighchartsObject } from '../highcharts/highchart-init';
import { MobileHighchartConfig } from '../highcharts/mobile-highchart-config';
import { ChartEventTypes } from '../index';
import { ChartThemeService, } from '../theme/chart-theme-service';
import { AxisTypes, ChartResponsiveLayout, ChartType, } from '../types';
import { createLogger } from '../utils/logger';
import { BlinkInsightchartConfig } from './blink-insightchart-config';
var Orientation;
(function (Orientation) {
    Orientation["Top"] = "top";
    Orientation["Bottom"] = "bottom";
    Orientation["Left"] = "left";
    Orientation["Right"] = "right";
})(Orientation || (Orientation = {}));
const logger = createLogger('highchart-renderer');
function setChartContainer(highchartConfig, el) {
    _.set(highchartConfig, ['chart', 'renderTo'], el);
}
function executeEventHandler(eventsMap, eventType, args = []) {
    const eventHandler = eventsMap.get(eventType);
    if (_.isFunction(eventHandler)) {
        eventHandler.call(null, ...args);
        return;
    }
    logger.warn(`Chart Event Callback not implemented for ${eventType}.`);
}
function bringSelectedSeriesToFront(point) {
    if (point && point.series && point.series.group) {
        point.series.group.toFront();
    }
}
function getHighchartOnSeriesClickHandler(handler, shouldBringSelectedSeriesToFront, viz) {
    return evt => {
        if (shouldBringSelectedSeriesToFront) {
            bringSelectedSeriesToFront(evt.point);
        }
        if (_.isFunction(handler)) {
            const clickedPoint = evt.point && getContextMenuInputPoint(viz, evt.point);
            handler(evt, clickedPoint);
        }
    };
}
export function getInsightChartConfig(chartModel, dataModel, boundingBox, eventsMap, config, env, onRender = _.noop) {
    const Highcharts = getHighchartsObject();
    ChartThemeService.useDefaultTheme(Highcharts, env.flags);
    const chartConfigOptions = configOptions(getChartType(chartModel), chartModel);
    const shouldBringSelectedSeriesToFront = chartConfigOptions.shouldBringSelectedSeriesToFront;
    const updateZoomAttributes = eventsMap.get(ChartEventTypes.UpdateZoomEvent);
    const chartConfig = new BlinkInsightchartConfig(chartModel, dataModel, {
        containerDimensions: boundingBox,
        shouldShowXAxisLabels: getDefaultLabelEnablementValues()
            .shouldShowXAxisLabels,
        shouldShowYAxisLabels: getDefaultLabelEnablementValues()
            .shouldShowYAxisLabels,
        shouldShowDataLabels: true,
        disableTransformations: config.disableTransformations,
        disableColorRotation: config.disableColorRotation,
        isDataSourceDefault: config.isDataSourceDefault,
        hideAxisIcon: config.hideAxisIcon,
        shouldShowXAxisName: true,
        shouldShowYAxisName: true,
    }, env);
    const xGridlineEnabled = config.xGridlineEnabled;
    const yGridlineEnabled = config.yGridlineEnabled;
    const shouldShowTotalRows = config.shouldShowDataRowDisplayedCount;
    const afterSetExtremesDebounce = _.debounce($event => {
        const axisesExtremes = $event.target.chart.getAxisesExtremes();
        updateZoomAttributes(axisesExtremes, !!($event.userMax || $event.userMin));
    }, 300);
    chartConfig
        .configureDefaults()
        .setColors()
        .disableZoom()
        .configureXAxis(xGridlineEnabled)
        .configureYAxis(yGridlineEnabled)
        .configureColorAxis()
        .configureSeries(getHighchartOnSeriesClickHandler(eventsMap.get(ChartEventTypes.PointClick), shouldBringSelectedSeriesToFront, chartModel), getHighchartOnSeriesClickHandler(eventsMap.get(ChartEventTypes.ContextClick), shouldBringSelectedSeriesToFront, chartModel), getHighchartOnSeriesClickHandler(eventsMap.get(ChartEventTypes.PointDoubleClick), shouldBringSelectedSeriesToFront, chartModel), getHighchartOnSeriesClickHandler(eventsMap.get(ChartEventTypes.MouseOver), shouldBringSelectedSeriesToFront, chartModel), getHighchartOnSeriesClickHandler(eventsMap.get(ChartEventTypes.MouseOut), shouldBringSelectedSeriesToFront, chartModel), env.flags.maxChartLegendCardinality, (series, visible) => { }, config.disableHighlightOnHover)
        .configureDataLabels()
        .configureConnectNulls()
        .configurePlotLines()
        .setAfterSetExtremesCallback($event => {
        if ($event.trigger && $event.trigger === 'zoom') {
            afterSetExtremesDebounce($event);
        }
    })
        .configureChartTitle(shouldShowTotalRows, config.shouldShowHighchartTitle)
        .configureWindowing()
        .configureLineOptions()
        .configureGenericTooltip()
        .configureExporting()
        .configureLegend((param) => { }, config.isLegendEnabled, hasPersistentLegend(getChartType(chartModel)), (serie, anchor) => {
    })
        .configureAxisScroll(getEnvFlags().allowMobileOverride)
        .configureChartOptions(getHighchartOnSeriesClickHandler(eventsMap.get(ChartEventTypes.ChartAreaClick), shouldBringSelectedSeriesToFront, chartModel))
        .configureSerieGetter(serieName => getSerie(dataModel, serieName))
        .configureRenderCallback(onRender);
    if (config.optSupportAnimation) {
        chartConfig.configureAnimation();
    }
    chartConfig.configureOverrides();
    if (env.flags.allowMobileOverride) {
        const mobileOverrides = new MobileHighchartConfig(chartModel);
        mobileOverrides.dropGraySelected().setScatterChartOverrides();
        _.merge(chartConfig._highchartConfig, mobileOverrides._highchartConfig);
    }
    const chartAxisDetails = {
        x: chartConfig._highchartConfig.xAxis,
        y: chartConfig._highchartConfig.yAxis,
    };
    const chartContainerWidth = chartConfig._containerDimensions.width;
    const defaultScreenConfig = { xGridlineEnabled, yGridlineEnabled };
    const chartConfigForScreen = getHighchartScreenConfig(chartContainerWidth, defaultScreenConfig, chartAxisDetails, config.shouldScaleFonts, chartModel, dataModel, config === null || config === void 0 ? void 0 : config.enablePiechartAnimation);
    if (hasNativeLegend(chartModel)) {
        chartConfigForScreen.config.legend.enabled =
            chartConfigForScreen.meta.legendEnabled;
    }
    chartConfig._highchartConfig = _.merge(chartConfig._highchartConfig, chartConfigForScreen.config);
    if (getEnvFlags().isHighchartCustomisationEnabled) {
        chartConfig.configureHighchartCustomisationSettings();
        chartConfig.configAdvancedAxisSettings();
        chartConfig.configAdvancedColumnSettings();
    }
    return chartConfig;
}
export function updateExtremes(axis, extremes) {
    if (!extremes) {
        return;
    }
    const { dataMin, dataMax, min, max } = axis;
    const shouldStartOnTick = _.isNil(extremes.min) && dataMin > min;
    const shouldEndOnTick = _.isNil(extremes.max) && dataMax < max;
    axis.options.startOnTick = shouldStartOnTick;
    axis.options.endOnTick = shouldEndOnTick;
    if (!shouldStartOnTick || !shouldEndOnTick) {
        axis.update({
            startOnTick: shouldStartOnTick,
            endOnTick: shouldEndOnTick,
        }, false);
    }
    axis.setExtremes(extremes.min, extremes.max, true);
}
function setAxisRange(chart, chartModel) {
    if (chart.userOptions.chart.type.toUpperCase() === ChartType.Treemap ||
        chart.userOptions.chart.type.toUpperCase() === ChartType.Heatmap ||
        chartModel.config.chartType === ChartType.Kpi) {
        return false;
    }
    const axisProperties = getAxisProperties(chartModel);
    if (axisProperties) {
        let isUpdated = false;
        axisProperties.forEach(axisObj => {
            const { yAxisRange, axisType } = axisObj.properties;
            chart.axes.forEach(axis => {
                if (axis.userOptions.id === axisObj.id &&
                    axis.hasVisibleSeries) {
                    const isNilRange = _.isNil(yAxisRange === null || yAxisRange === void 0 ? void 0 : yAxisRange.min) && _.isNil(yAxisRange === null || yAxisRange === void 0 ? void 0 : yAxisRange.max);
                    if (!_.isNil(yAxisRange) && !isNilRange) {
                        const props = chart.userOptions.chart.props;
                        const updatedYAxisRange = getUpdatedAxisRangeBasedOnTicks(axisObj.id, props, yAxisRange, axis);
                        isUpdated = true;
                        updateExtremes(axis, updatedYAxisRange);
                    }
                    else if (axisType === AxisTypes.Y) {
                        const { dataMin, dataMax, min, max } = axis;
                        axis.update({
                            startOnTick: dataMin > min,
                            endOnTick: dataMax < max,
                        }, false);
                    }
                }
            });
        });
        return isUpdated;
    }
    return false;
}
export function updateChartZoom(chart, chartModel) {
    const chartProperties = getChartProperties(chartModel);
    if (!chart || !isZoomedIn(chartModel)) {
        return false;
    }
    const xAxisExtremes = chartProperties.axisExtremes.x;
    const yAxisExtremes = chartProperties.axisExtremes.y;
    chart.setExtremes(xAxisExtremes, yAxisExtremes);
    return true;
}
function configureAxisControls(chartEl, eventsMap, chartModel) {
    const isXAxisVertical = chartTypeSpecificationService.isXAxisVertical(getChartType(chartModel));
    const popoverXDirection = isXAxisVertical
        ? Orientation.Right
        : Orientation.Top;
    const isYAxisVertical = !isXAxisVertical;
    const xAxes = getXAxes(chartModel);
    const yAxesInChart = getYAxes(chartModel);
    const onAxisClick = eventsMap.get(ChartEventTypes.AxisClick);
    const onColumnSort = eventsMap.get(ChartEventTypes.ColumnSort);
    bindAxisEvents(chartEl, chartModel, onAxisClick, onColumnSort);
}
export function updateChartModelProperties(chartObj, chartModel, dataModel, chartConfig, chart, updateChartProperties) {
    const chartType = getChartType(chartModel);
    const { isMultiColorSeries } = configOptions(chartType, chartModel);
    let seriesColors = getSeriesColorsMap(chartModel);
    let needsUpdate = chartConfig.isSeriesColorUpgradedNeeded;
    let isColorUpdatedNeeded = false;
    chart.series.forEach(serie => {
        const seriesId = (serie.userOptions || serie).blinkSeriesId;
        if (isMultiColorSeries) {
            const pointArray = serie.points;
            pointArray.forEach(point => {
                const serieColor = getColorBySerieName(seriesColors, point.name);
                if (!serieColor ||
                    (point.color && serieColor !== point.color)) {
                    seriesColors = getUpdatedSeriesColor(seriesColors, point.name, point.color);
                    needsUpdate = true;
                    isColorUpdatedNeeded = true;
                }
            });
        }
        else {
            const serieColor = getColorBySerieName(seriesColors, seriesId);
            if (!serieColor && seriesId !== undefined) {
                seriesColors = getUpdatedSeriesColor(seriesColors, seriesId, serie.color);
                needsUpdate = true;
                isColorUpdatedNeeded = true;
            }
        }
        const chartModelSerie = getSerie(dataModel, serie.userOptions.name);
        if (chartModelSerie && !chartModelSerie.color) {
            chartModelSerie.color = serie.color;
        }
    });
    let updatedClientState = { ...chartModel.vizProp };
    if (isColorUpdatedNeeded) {
        needsUpdate = true;
        updatedClientState = {
            ...updatedClientState,
            systemSeriesColors: seriesColors,
        };
    }
    const responsiveLayoutPreference = chartConfig._responsiveLayoutPreference;
    const chartProperties = updatedClientState.chartProperties || {};
    if (chartProperties.responsiveLayoutPreference !==
        responsiveLayoutPreference &&
        (responsiveLayoutPreference === ChartResponsiveLayout.AutoOn ||
            responsiveLayoutPreference === ChartResponsiveLayout.AutoOff)) {
        needsUpdate = true;
        updatedClientState = {
            ...updatedClientState,
            chartProperties: {
                ...chartProperties,
                responsiveLayoutPreference,
            },
        };
    }
    if (needsUpdate) {
        updateChartProperties(updatedClientState);
        chartConfig.isSeriesColorUpgradedNeeded = false;
        _.set(chartObj.getVizModel(), 'vizProp', updatedClientState);
    }
}
export function chartUIRenderInsightHighchart(chartObj, chartEl, chartModel, dataModel, eventsMap, config, env) {
    try {
        const ctrBoundingBox = chartEl.getBoundingClientRect();
        const onRender = () => {
            configureAxisControls(chartEl, eventsMap, chartModel);
            executeEventHandler(eventsMap, ChartEventTypes.RenderComplete);
        };
        const updateChartProperties = (vizProp) => {
            executeEventHandler(eventsMap, ChartEventTypes.UpdateClientState, [
                vizProp,
                false,
                false,
            ]);
        };
        const blinkInsightchartConfig = getInsightChartConfig(chartModel, dataModel, ctrBoundingBox, eventsMap, config, env, onRender);
        const chartConfig = blinkInsightchartConfig.build();
        setChartContainer(chartConfig, chartEl);
        logger.debug('chartConfig', chartConfig.toString(), chartConfig);
        const blinkChart = new BlinkHighchart(chartConfig, onRender);
        updateChartModelProperties(chartObj, chartModel, dataModel, blinkInsightchartConfig, blinkChart, updateChartProperties);
        let needRedraw = setAxisRange(blinkChart, chartModel);
        if (!needRedraw) {
            needRedraw = updateChartZoom(blinkChart, chartModel);
        }
        if (needRedraw) {
            blinkChart.lightRedraw();
        }
        return Promise.resolve(blinkChart);
    }
    catch (err) {
        logger.error('Failed to load the chart due to highchart errors', err);
        throw new Error(err);
    }
}
//# sourceMappingURL=insight-chart-utils.js.map